/**
 * The small part of markdown these notes actually contain.
 *
 * WHY NOT A MARKDOWN LIBRARY
 *
 * Because this is not markdown. It is notes written in a house style, and the
 * one thing that matters most in them — `#205` becoming a link to that issue —
 * is not a markdown feature at all. A general renderer would have to be taught
 * that anyway, and would bring headings, tables, images and raw HTML that
 * nothing here writes and that nobody has decided how to display.
 *
 * WHAT IS SUPPORTED, AND WHY EXACTLY THIS
 *
 * Counted across all 378 written fields on the two boards:
 *
 *     101  **bold**            80  `code`          71  - bullets
 *      13  [[#205]]             6  1. numbered      3  ```fenced```
 *       0  ## headings          0  [text](url)      0  > quotes
 *
 * The last row is why headings, links and block quotes are not here. Building
 * for them would be building for a guess; they can be added the day something
 * writes one.
 *
 * Returns a structure rather than markup, so what it decides can be checked
 * without a browser — see scripts/test-markdown.ts.
 */

/** A reference to another issue: #200, #187-A, B4. */
const REF = String.raw`#\d+[a-zA-Z-]*|\bB\d+\b`;

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'bold'; parts: Inline[] }
  | { kind: 'ref'; ref: string };

export type Block =
  | { kind: 'para'; parts: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] }
  | { kind: 'pre'; text: string };

/**
 * One line of text, split into what it is made of.
 *
 * ORDER MATTERS AND IS NOT ARBITRARY. Code spans are taken first, so a
 * reference written inside backticks — `#200`, meaning the literal text — is
 * left alone rather than turned into a link to an issue the writer was only
 * naming. That case is real: these notes quote references while discussing
 * them.
 *
 * `inBold` stops bold nesting inside itself for ever; everything else is still
 * recognised inside bold, because "**Blocked by #205**" is written constantly.
 */
function inline(text: string, inBold = false): Inline[] {
  const pattern = inBold
    ? new RegExp(String.raw`(\`[^\`\n]+\`)|(\[\[[^\]\n]+\]\])|(${REF})`, 'g')
    : new RegExp(String.raw`(\`[^\`\n]+\`)|(\*\*[^*\n]+\*\*)|(\[\[[^\]\n]+\]\])|(${REF})`, 'g');

  const out: Inline[] = [];
  let at = 0;
  for (const m of text.matchAll(pattern)) {
    const start = m.index ?? 0;
    if (start > at) out.push({ kind: 'text', text: text.slice(at, start) });
    const token = m[0];
    if (token.startsWith('`')) {
      out.push({ kind: 'code', text: token.slice(1, -1) });
    } else if (token.startsWith('**')) {
      out.push({ kind: 'bold', parts: inline(token.slice(2, -2), true) });
    } else if (token.startsWith('[[')) {
      // A wiki-style reference. The brackets are notation, not content — they
      // used to be printed either side of the link, which is nobody's intent.
      const inner = token.slice(2, -2).trim();
      out.push(
        new RegExp(`^(?:${REF})$`).test(inner)
          ? { kind: 'ref', ref: inner }
          : { kind: 'text', text: inner }
      );
    } else {
      out.push({ kind: 'ref', ref: token });
    }
    at = start + token.length;
  }
  if (at < text.length) out.push({ kind: 'text', text: text.slice(at) });
  return out;
}

const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;

/** The whole note, as blocks. */
export function parseNotes(text: string): Block[] {
  const lines = (text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const endPara = () => {
    if (para.length) blocks.push({ kind: 'para', parts: inline(para.join('\n')) });
    para = [];
  };
  const endList = () => {
    if (list) blocks.push({ kind: 'list', ordered: list.ordered, items: list.items.map((i) => inline(i)) });
    list = null;
  };
  const endAll = () => {
    endPara();
    endList();
  };

  for (let n = 0; n < lines.length; n++) {
    const line = lines[n];

    // Fenced code runs to its closing fence, or to the end if it never closes —
    // an unclosed fence is a half-written note, not a reason to show nothing.
    if (line.trimStart().startsWith('```')) {
      endAll();
      const body: string[] = [];
      n++;
      while (n < lines.length && !lines[n].trimStart().startsWith('```')) body.push(lines[n++]);
      blocks.push({ kind: 'pre', text: body.join('\n') });
      continue;
    }

    if (!line.trim()) {
      endAll();
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = NUMBER.exec(line);
    if (bullet || numbered) {
      const ordered = !!numbered;
      endPara();
      // A numbered list starting where a bulleted one left off is a new list.
      if (list && list.ordered !== ordered) endList();
      list = list ?? { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1]);
      continue;
    }

    endList();
    para.push(line);
  }
  endAll();
  return blocks;
}
